import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import Slider from '@mui/material/Slider';

import DialogTitleCommon, {
  DialogCloseCommon,
} from "components/Dialogs/DialogTitleCommon";
import { FormButton } from "components/FormComponents/Button/FormButton";
import { adminStyles } from "features/Administration/components/adminStyles";
import { CPSConstants, CPSNumbers, SurveyQuestionTypes } from "commonUtils/enumerations";
import { additionalQuestionDetailList, CPSQuestionResponse, NameProps, QuestionControlResponse } from "commonUtils/genericInterfaces";
import DOMPurify from "dompurify";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import KeyboardDoubleArrowUpOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowUpOutlined';
import KeyboardDoubleArrowDownOutlinedIcon from '@mui/icons-material/KeyboardDoubleArrowDownOutlined';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Checkbox from "@mui/material/Checkbox";
import { LightTooltip } from "components/Tooltip/LightTooltip";
import CPSLogo from "../../assets/cps_logo_white.webp";
import {
  initialMessage,
  messages,
  initialMessages,
  getServerMessages,
  isValidObject,
  scrollToTop
} from "commonUtils/utils";
import {
  Alerts,
  CPSMessageCodes
} from "commonUtils/enumerations";

import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import { Loader } from "components/FormComponents/Loader/Loader";

type valueMarks = {
  value: number;
  label: string;
}

type ProgressDot = {
  key: number;
  filled: boolean;
  active: boolean;
}

type AdditionalQuestionResponse = additionalQuestionDetailList & {
  followUpRequired: boolean;
  showReason: boolean;
}
type mcqOption = {
  fieldId: number;
  fieldLabel: string;
}

const SurveyQuestionDialog = ({
  errorCode,
  loader,
  openPopup,
  handlePopupClose,
  popUpTitle,
  handleSubmit,
  questionDetail,
  questionControlData,
  handlePrevious
}: {
  errorCode: number;
  loader: boolean;
  openPopup: boolean;
  handlePopupClose?: any;
  popUpTitle?: string;
  handleSubmit?: any;
  questionDetail: CPSQuestionResponse;
  questionControlData?: QuestionControlResponse;
  handlePrevious?: any;
}) => {
  const [popLoader, setPopLoader] = useState<boolean>(loader);
  const [selectedRadio, setSelectedRadio] = useState<number>(0);
  const [ratingSacleValue, setRatingSacleValue] = useState<number>(0);
  const [ratingScaleLevels, setRatingScaleLevels] = useState<valueMarks[]>([]);
  const [progressDot, setProgressDot] = useState<ProgressDot[]>([]);
  const [showFollowUpQuestion, setShowFollowUpQuestion] = useState<boolean>(false);
  const [showPreviousButton, setShowPreviousButton] = useState<boolean>(false);
  const [showSubmitButton, setShowSubmitButton] = useState<boolean>(true);
  const [gridColums, setGridColums] = useState<NameProps[]>([]);
  const [subQuestionResponse, setSubQuestionResponse] = useState<AdditionalQuestionResponse[]>([]);
  const [textChar, setTextChar] = useState<string>("");
  const [followupChar, setFollowupChar] = useState<string>("");
  const [submitBtnText, setSubmitBtnText] = useState<string>("Submit");
  const [showAlert, setShowAlert] = React.useState(false);
  const [servervalidation, setServerValidation] = React.useState(initialMessage);
  const [serverMessagesList, setServerMessagesList] = React.useState<messages[]>(initialMessages);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const dialogContentRef = React.useRef<HTMLDivElement>(null);
  const [selectedMCQValues, setSelectedMCQValues] = useState<number[]>([]);
  const [showMultiFollowup, setShowMultiFollowup] = useState<boolean>(false);
  const [followUpText, setFollowUpText] = useState("");
  const [otherText, setOtherText] = useState("");


  const mcqOptions = React.useMemo(() => {
    if (!questionDetail.mcqQuestion) return [];

    const others = questionDetail.mcqQuestion.filter(
      o => o.fieldLabel?.toLowerCase() === CPSConstants.OTHER
    );

    const rest = questionDetail.mcqQuestion.filter(
      o => o.fieldLabel?.toLowerCase() !== CPSConstants.OTHER
    );

    return [...rest, ...others];
  }, [questionDetail.mcqQuestion]);

  const LoadMessages = async () => {
    try {
      const response = await getServerMessages(
        CPSMessageCodes.CPS_SURVEY_MESSAGES,
      );
      setServerMessagesList(response);
    } catch (error: any) {

    }
  };
useEffect(() => {
    if (questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ) {
      const id = Number(questionDetail.responseOptionId ?? 0);

      if (id > 0) {
        setSelectedMCQValues([id]);
      } else {
        setSelectedMCQValues([]);
      }
    }

    if (questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ) {
      const rawOptions = 
        questionDetail.mcqResponseOptionsId ?? 
        (questionDetail as Record<string, any>)['mcqResponseOptionsId'] ?? 
        "";

      const ids = String(rawOptions)
        .split(",")
        .map((x: string) => Number(x.trim()))
        .filter((n: number) => !Number.isNaN(n) && n > 0);

      if (ids.length > 0) {
        setSelectedMCQValues(ids);
      } else {
        setSelectedMCQValues([]);
      }
    }
   
    setFollowUpText(questionDetail.followUpResponseText ?? "");
    setOtherText(questionDetail.otherOptionMCQText ?? "");
  }, [
    questionDetail.responseId,
    questionDetail.responseOptionId,
    questionDetail.mcqResponseOptionsId,
    (questionDetail as Record<string, any>)['mcqResponseOptionsId'],
    questionDetail.questionId
  ]);
  const getMessage = (messageCode: number) => {
    const findVal = serverMessagesList?.find(
      v => v.messageCode === messageCode,
    );
    return findVal || initialMessage;
  };
  const ITEMS_PER_COLUMN = 15;

  const chunkedOptions = [];
  for (let i = 0; i < mcqOptions.length; i += ITEMS_PER_COLUMN) {
    chunkedOptions.push(mcqOptions.slice(i, i + ITEMS_PER_COLUMN));
  }


  const isMcqFollowUpRequired = React.useMemo(() => {
    const followUpIds =
      (questionDetail.followUpOptionIds ?? "")
        .split(",")
        .map(x => Number(x.trim()))
        .filter(n => !Number.isNaN(n) && n > 0);

    if (followUpIds.length === 0) return false;
    return selectedMCQValues.some(id => followUpIds.includes(id));
  }, [questionDetail.followUpOptionIds, selectedMCQValues]);


  const responseSubmit = () => {
    setPopLoader(true);

    let finalResponseText = "";

    if (
      questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ ||
      questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ
    ) {
      finalResponseText = mcqOptions
        .filter(o => selectedMCQValues.includes(o.fieldId))
        .map(o => o.fieldLabel)
        .join(",");
    } else {
      finalResponseText = questionDetail.responseText ?? "";
    }


    if (questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ) {
      questionDetail.responseOptionId = selectedMCQValues[0] ?? 0;
    }

    if (questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ) {
      questionDetail.mcqResponseOptionsId = selectedMCQValues.join(",");
    }

if (
  questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ ||
  questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ) {
  if (isOtherSelected() && (!otherText || otherText.trim() === "")) {
    showAlertAtTop(79669112);
    
    return;
  }
}
    const updatedQuestionDetail: CPSQuestionResponse = {
      ...questionDetail,
      responseText: finalResponseText,
      mcqResponseOptionsId:
        questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ
          ? selectedMCQValues.join(",")
          : (questionDetail.mcqResponseOptionsId ?? ""),
      responseOptionId: questionDetail.responseOptionId ?? 0,
    };


    if (
      updatedQuestionDetail.isRequired &&
      !(updatedQuestionDetail.showNotif || updatedQuestionDetail.showIntro)
    ) {
      ValidateQuestionResponse();
      return;
    }

    setShowAlert(false);
    handleSubmit(updatedQuestionDetail);
  };

  const previousSubmit = () => {
    setPopLoader(true);
    handlePrevious();
    setShowAlert(false);
  };
  useEffect(() => {
    LoadMessages();
  }, []);
  useEffect(() => {
    if (errorCode > 0) {
      showAlertAtTop(errorCode);
    }
  }, [errorCode]);

  const showAlertAtTop = (errorCode: number) => {
    setShowAlert(true);
    setServerValidation(getMessage(errorCode));
    scrollToTop();
    setPopLoader(false);
  }

  useEffect(() => {
    getProgressDots();
    setSubmitButtonText();
    setShowFollowUpQuestion(false);
    setShowSubmitButton(true);
    setShowPreviousButton(questionDetail.questionNumber > 1 && !(questionDetail.showIntro || questionDetail.showNotif));
    if (questionDetail.optionIds && questionControlData?.ratingScaleOptions
      && questionDetail.questionType === SurveyQuestionTypes.Grid) {
      const columns: NameProps[] = [];
      questionDetail.optionIds.split(',').filter((ids: string) => {
        const option = questionControlData.ratingScaleOptions.find((item: NameProps) => item.id === Number(ids));
        if (option) {
          columns.push(option);
        }
      });
      setGridColums(columns);
    }

    if (questionDetail.questionType === SurveyQuestionTypes.Radio && questionDetail.responseOptionId === CPSNumbers.ZERO) {
      setShowSubmitButton(false);
    } else if (questionDetail.questionType === SurveyQuestionTypes.Radio && questionDetail.responseOptionId !== CPSNumbers.ZERO) {
      setShowSubmitButton(true);
    }
    if (
      questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ ||
      questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ
    ) {
      setShowSubmitButton(true);
    }


    if (questionDetail.optionIds && questionControlData?.ratingScaleOptions && questionDetail.questionType === SurveyQuestionTypes.RatingScale) {
      const rating: valueMarks[] = [];
      let indexCounter = 0;
      questionDetail.optionIds.split(',').filter((ids: string) => {
        let ls_item = questionControlData.ratingScaleOptions.find((item: NameProps) => item.id === Number(ids));
        if (ls_item) {
          rating.push({
            label: ls_item.name,
            value: (++indexCounter)
          } as valueMarks);
          return true;
        } else {
          return false;
        }
      });
      setRatingScaleLevels(rating);

    }
    if (questionDetail.questionType === SurveyQuestionTypes.Numeric && questionDetail.rangeEnd && questionDetail.rangeFrom) {
      const rating = [];
      for (let i = Number(questionDetail.rangeFrom); i <= Number(questionDetail.rangeEnd); ++i) {
        rating.push({ label: `${i}`, value: i } as valueMarks);
      }
      setRatingScaleLevels(rating);
    }
    setResponses();

    setPopLoader(false);
  }, [questionDetail.questionId, questionDetail.webDisplayOrder, questionDetail.showNotif, questionDetail.showIntro]);

  const isOtherSelected = () => {
    return mcqOptions.some(
      opt =>
        opt.fieldLabel?.toLowerCase() === CPSConstants.OTHER &&
        selectedMCQValues.includes(opt.fieldId)
    );
  };
const followUpOptionIdsArr = React.useMemo(() => {
  return (questionDetail.followUpOptionIds ?? "")
    .split(",")
    .map(x => Number(x.trim()))
    .filter(n => !Number.isNaN(n) && n > 0);
}, [questionDetail.followUpOptionIds]);

const isMcqFollowUpRequiredFor = (ids: number[]) => {
  if (followUpOptionIdsArr.length === 0) return false;
  return ids.some(id => followUpOptionIdsArr.includes(id));
};

const canAutoSaveMcq = (ids: number[]) => {
  if (isMcqFollowUpRequiredFor(ids)) return false;

  const otherOpt = mcqOptions.find(
    o => o.fieldLabel?.toLowerCase() === CPSConstants.OTHER
  );
  if (otherOpt && ids.includes(otherOpt.fieldId)) return false;

  return true;
};
const submitMcqWithIds = (ids: number[]) => {
  setPopLoader(true);

  const finalResponseText = mcqOptions
    .filter(o => ids.includes(o.fieldId))
    .map(o => o.fieldLabel)
    .join(",");

  const updatedQuestionDetail: CPSQuestionResponse = {
    ...questionDetail,
    responseText: finalResponseText,
    responseOptionId:
      questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ
        ? (ids[0] ?? 0)
        : (questionDetail.responseOptionId ?? 0),
    mcqResponseOptionsId:
      questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ
        ? ids.join(",")
        : (questionDetail.mcqResponseOptionsId ?? ""),
    otherOptionMCQText: "",
    followUpResponseText: "",
  };

  setShowAlert(false);
  handleSubmit(updatedQuestionDetail);
};

  const ValidateQuestionResponse = () => {
    if (questionDetail.questionType === SurveyQuestionTypes.Text) {
      if (textChar === null || textChar === '') {
        showAlertAtTop(7966979);
      } else {
        setShowAlert(false);
        handleSubmit(questionDetail);
      }
    }
    else if (questionDetail.questionType === SurveyQuestionTypes.Numeric || questionDetail.questionType === SurveyQuestionTypes.RatingScale) {
      var responseoptionId = questionDetail.responseOptionId ?? 0;
      if (responseoptionId === null || responseoptionId === 0) {
        showAlertAtTop(7966980);
      }
      else {
        setShowAlert(false);
        handleSubmit(questionDetail);
      }
    }
    else if (questionDetail.questionType === SurveyQuestionTypes.Radio || questionDetail.questionType === SurveyQuestionTypes.Consent) {
      var responseoptionId = questionDetail.responseOptionId ?? 0;
      if (responseoptionId === null || responseoptionId === 0) {
        showAlertAtTop(7966980);
      }
      else {
        setShowAlert(false);
        handleSubmit(questionDetail);
      }
    } else
      if (questionDetail.questionType === SurveyQuestionTypes.Grid) {
        let isValid = true;

        if (questionDetail.additionalQuestionDetailList === null || questionDetail.additionalQuestionDetailList.length === 0) {
          showAlertAtTop(7966981);
          setShowFollowUpQuestion(true);
          isValid = false;
        }
        else if (questionDetail.additionalQuestionDetailList.length > 0) {
          const additional = questionDetail.additionalQuestionDetailList;
          additional?.forEach((subQuestion, index) => {
            if (subQuestion.responseOptionId === null || subQuestion.responseOptionId === 0) {
              showAlertAtTop(7966981);
              setShowFollowUpQuestion(true);
              isValid = false;
            }
          });
          if (isValid) {
            setShowAlert(false);
            handleSubmit(questionDetail);
          } else {
            setPopLoader(false);
          }
        }
      }
      else if (
        questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ ||
        questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ
      ) {
        if (
          questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ &&
          (!questionDetail.responseOptionId || questionDetail.responseOptionId === 0)
        ) {
          showAlertAtTop(7966980);
          return;
        }

        if (
          questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ &&
          (!questionDetail.mcqResponseOptionsId || questionDetail.mcqResponseOptionsId === "")
        ) {
          showAlertAtTop(7966980);
          return;
        }

        setShowAlert(false);
        handleSubmit(questionDetail);
      }
      else {
        setShowAlert(false);
        handleSubmit(questionDetail);
      }
  }
  useEffect(() => {
    const shouldShow =
      Boolean(questionDetail.followUpQuestionTextHTML) && isMcqFollowUpRequired;

    setShowMultiFollowup(shouldShow);

  }, [isMcqFollowUpRequired, questionDetail.followUpQuestionTextHTML]);
  const setSubmitButtonText = () => {
    if (questionDetail?.showIntro || questionDetail?.questionType === SurveyQuestionTypes.Consent) {
      setSubmitBtnText('Continue');
    }
    else if (questionDetail?.showNotif) {
      setSubmitBtnText('Close');
    }
    else if (questionDetail?.totalQCount && questionDetail?.questionNumber < questionDetail?.totalQCount) {
      setSubmitBtnText('Save/Next');
    }
    else {
      setSubmitBtnText('Submit');
    }
  }

  const setResponses = () => {
    switch (questionDetail.questionType) {
      case SurveyQuestionTypes.Text:
        setTextChar(questionDetail.responseText ?? "");
        break;
      case SurveyQuestionTypes.Consent:
        setFollowupChar(questionDetail.responseText ?? "");
        break;
      case SurveyQuestionTypes.Numeric:
        setFollowupChar(questionDetail.responseText ?? "");
        setRatingSacleValue(questionDetail.responseOptionId ?? questionDetail.rangeFrom);
        setRatingScaleFollowup(questionDetail.responseOptionId ?? questionDetail.rangeFrom);
        break;
      case SurveyQuestionTypes.Grid:
        if (questionDetail.additionalQuestionDetailList && questionDetail.additionalQuestionDetailList.length > 0) {
          setSubQuestionResponse(questionDetail.additionalQuestionDetailList?.map(x => {
            let followUp = false;
            if (questionDetail.followUpOptionIds && questionDetail.followUpOptionIds.length > 0) {
              followUp = questionDetail.followUpOptionIds.split(',').findIndex(fol => Number(fol) === x.responseOptionId) > -1
            }
            return {
              ...x,
              followUpRequired: followUp,
              showReason: true,
            };
          }));
        }
        break;
      case SurveyQuestionTypes.Radio:
        setFollowupChar(questionDetail.responseText ?? "");
        setSelectedRadio(questionDetail.responseOptionId ?? 0);
        if (questionDetail.followUpOptionIds && questionDetail.followUpOptionIds.length > 0) {
          setShowFollowUpQuestion(questionDetail.followUpOptionIds.split(',').findIndex(x => Number(x) === (questionDetail.responseOptionId ?? 0)) > -1)
        } else {
          setShowFollowUpQuestion(false);
        }
        break;
      case SurveyQuestionTypes.RatingScale:
        setFollowupChar(questionDetail.responseText ?? "");
        if (questionControlData?.ratingScaleOptions) {
          let rating: valueMarks[] = [];
          let indexCounter = 0;
          questionDetail.optionIds.split(',').filter((ids: string) => {
            let ls_item = questionControlData.ratingScaleOptions.find((item: NameProps) => item.id === Number(ids));
            if (ls_item) {
              rating.push({
                label: ls_item.id,
                value: (++indexCounter)
              } as valueMarks);
              return true;
            } else {
              return false;
            }
          });
          if (questionDetail.responseOptionId != 0) {
            const ratingValue = rating.find(x => Number(x.label) === questionDetail.responseOptionId)?.value ?? rating[0].value;
            const ratinglable = rating.find(x => Number(x.label) === questionDetail.responseOptionId)?.label ?? rating[0].label;
            setRatingSacleValue(ratingValue ? ratingValue : CPSNumbers.ZERO);
            setRatingScaleFollowup(Number(ratinglable) ? Number(ratinglable) : CPSNumbers.ONE);
          } else {
            setRatingSacleValue(questionDetail.responseOptionId);
            setRatingScaleFollowup(questionDetail.responseOptionId);
          }
        }
        break;

      default:
        break;
    }
  }

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = Number(event.target.value);
    questionDetail.responseOptionId = value;

    if (event.target.checked) {
      setSelectedRadio(value);
    }
    if (questionDetail.responseOptionId === CPSConstants.SurveyDeclined) {
      setSubmitBtnText('Close');
    } else {
      setSubmitButtonText();
    }
    if (questionDetail.questionType !== SurveyQuestionTypes.Consent) {
      if (questionDetail.followUpOptionIds && questionDetail.followUpOptionIds.length > 0 && questionDetail.followUpOptionIds.split(',').findIndex(x => Number(x) === value) > -1) {
        setShowFollowUpQuestion(true);
        setShowSubmitButton(true);

      } else {
        setShowFollowUpQuestion(false);
        setShowSubmitButton(false);
        responseSubmit();
      }
    }

  }

  const setRatingScaleFollowup = (selectedval: number) => {
    if (questionDetail.followUpOptionIds && questionDetail.followUpOptionIds.length > 0) {
      setShowFollowUpQuestion(questionDetail.followUpOptionIds.split(',').findIndex(x => Number(x) === selectedval) > -1);
    } else {
      setShowFollowUpQuestion(false);
    }
  }

  const handleRatingScaleChange = (event: React.SyntheticEvent | Event, value: number | number[]) => {
    setRatingSacleValue(Number(value));
    setShowAlert(false);

    if (questionDetail.questionType === SurveyQuestionTypes.RatingScale) {
      const ratingvalue = questionControlData?.ratingScaleOptions?.find(item =>
        ratingScaleLevels.find(l => l.value === value)?.label === item.name)?.id || 0;
      setRatingScaleFollowup(Number(ratingvalue));
      questionDetail.responseOptionId = Number(ratingvalue);
    } else {
      questionDetail.responseOptionId = Number(value);
      setRatingScaleFollowup(Number(value));
    }
  }
 const scrollToTop = () => {
  if (dialogContentRef?.current) {
    dialogContentRef.current.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
};
  const handleGridRadioChange = (value: number, displayOrder: number) => {
    const index = subQuestionResponse.findIndex(x => x.displayOrder === displayOrder);
    let followUp = false
    if (questionDetail.followUpOptionIds && questionDetail.followUpOptionIds.length > 0) {
      followUp = questionDetail.followUpOptionIds.split(',').findIndex(x => Number(x) === value) > -1
    }
    if (index !== -1) {
      subQuestionResponse[index] = {
        ...subQuestionResponse[index],
        responseOptionId: value,
        followUpRequired: followUp,
        responseText: "",
      };
    }
    setSubQuestionResponse(prev => { return [...subQuestionResponse]; });
    questionDetail.additionalQuestionDetailList = subQuestionResponse;

  }
  const valuetext = (value: number) => {
    return (ratingScaleLevels.length > 0) ? `${ratingScaleLevels.find(l => l.value == value)?.label ?? value}` : `${value}`;
  }

  const sanitizeReplaceWhiteSpace = (text: string) => {
    return DOMPurify.sanitize(text).replaceAll("&nbsp;", " ");
  }
  const getProgressDots = () => {
    let progress: ProgressDot[] = [];
    if (questionDetail.totalQCount && questionDetail.questionNumber > 0) {
      for (let i = 1; i <= questionDetail.totalQCount; i++) {
        progress.push({
          key: i,
          filled: (i <= questionDetail.answeredCount),
          active: (i === (questionDetail.answeredCount + 1) )
        });
      }
      setProgressDot(prev => { return progress });
    }
  };

  return (
    <>
      <Dialog
        PaperProps={{
          sx: adminStyles.paperPropUpSurveyPreviewDialog,
        }}
        open={openPopup}>
        <DialogCloseCommon onClick={handlePopupClose} />
        <DialogTitleCommon title={popUpTitle} />
        <DialogContent
          ref={dialogContentRef}
          sx={questionDetail.questionType === SurveyQuestionTypes.Grid ? { scrollbarGutter: "stable" } : undefined}
        >
          <Loader open={popLoader} />
          <Box component="form" noValidate ref={scrollRef}>
            {showAlert && servervalidation.message && (
              <Box sx={{
                fontFamily: CPSConstants.FONT_FAMILY as string,
                padding: "0 0 1rem 0",
              }}>
                {
                  <FormValidationAlert
                    severity={
                      servervalidation?.isSuccessMessage
                        ? Alerts.SUCCESS
                        : servervalidation?.isErrorMessage
                          ? Alerts.ERROR
                          : Alerts.INFO
                    }
                    message={servervalidation?.message ?? ""}
                    closeIcon={true}
                    showAlertBox={showAlert}
                    setShowAlertBox={setShowAlert}
                  />
                }
              </Box>
            )}
            <Box sx={adminStyles.surveyTitleSection}>
              <Box>    <img
                src={CPSLogo}
                alt="CPS Logo"
                style={adminStyles.userProfileStyleCpsLogo}
              /></Box>
              <Typography style={{ fontWeight: 'bold', textAlign: 'center', fontSize: 'x-large', paddingRight: '120px', color: 'rgb(0, 20, 68)' }}>
                {questionDetail.title}
              </Typography>
              <Typography>
                {((questionDetail.questionNumber > 0 && !(questionDetail.showNotif || questionDetail.showIntro)) ? (`${questionDetail.questionNumber}/${questionDetail.totalQCount}`) : "")}
              </Typography>
            </Box>
            {(progressDot.length > 0 && !questionDetail.showIntro && !questionDetail.showNotif) && (
              <>
                <Grid container>
                  <Grid item xs={12}>
                    <Box id="questionProgress" className="progress-container">
                      {progressDot.length > 0 &&
                        progressDot.map((x: ProgressDot) => (
                          <Box
                            key={x.key}
                            className={[
                              'progress-dot',
                              x.filled ? 'filled' : '',
                              x.active ? 'active' : '',
                            ].join(' ').trim()}
                          />
                        ))}
                    </Box>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={12}>
                    <Box
                      sx={adminStyles.surveyQuestionNumber}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeReplaceWhiteSpace(`Question ${questionDetail.questionNumber}`),
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}
            <Grid container>
              {(questionDetail?.showIntro || questionDetail?.showNotif) ? (
                <Grid item xs={12} pb={0}>
                  <Box
                    sx={adminStyles.tableGridQuestionHeaderCellP}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeReplaceWhiteSpace(questionDetail.questionTextHTML || ""),
                    }}
                  />
                </Grid>
              ) : (<>

                {questionDetail.questionType === SurveyQuestionTypes.Consent && questionDetail.additionalQuestionDetailList && (
                  <Grid item xs={12} pb={0}>
                    <Box
                      sx={adminStyles.tableGridQuestionHeaderCellP}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeReplaceWhiteSpace(questionDetail.additionalQuestionDetailList?.[0].questionTextHTML || ""),
                      }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} pb={0}>
                  <Box
                    sx={adminStyles.tableGridQuestionHeaderCellP}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeReplaceWhiteSpace(questionDetail.questionTextHTML || ""),
                    }}
                  />
                </Grid>
                <Grid item xs={12} pt={0}>
                  <Box
                    sx={adminStyles.tableGridQuestionHeaderCellP}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeReplaceWhiteSpace(questionDetail.instructionTextHTML || ""),
                    }}
                  />
                </Grid>
                {questionDetail.questionType === SurveyQuestionTypes.Grid && (
                  <Grid item xs={12} mt={1} mb={2}>
                    <Table className="width-100cent" aria-label="alert table">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={adminStyles.tableGridQuestionHeaderCellButtomBorder}
                            key={`th_0_1`}>
                            <Box
                              sx={adminStyles.tableGridQuestionHeaderCellP}
                              dangerouslySetInnerHTML={{
                                __html: sanitizeReplaceWhiteSpace(
                                  questionDetail.headerTextHTML || "",
                                ),
                              }}
                            />

                          </TableCell>
                          {gridColums.map((val: NameProps, i: number) => (
                            <TableCell
                              sx={adminStyles.tableGridQuestionHeaderCell}
                              key={`th_${i}`}
                            >
                              {val.name}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {subQuestionResponse && subQuestionResponse.length > 0 && subQuestionResponse.map(
                          (question: AdditionalQuestionResponse, qi: number) => {
                            return (
                              <>
                                <TableRow key={`trGridQu_${qi}`}>
                                  <TableCell
                                    sx={adminStyles.tableGridQuestionCell}
                                    key={`thGridQu_${qi}`}
                                  >
                                    <Box className="inLineBlock">
                                      <Box
                                        sx={adminStyles.tableGridQuestionHeaderCellP}
                                        dangerouslySetInnerHTML={{
                                          __html: sanitizeReplaceWhiteSpace(
                                            question.questionTextHTML,
                                          ),
                                        }}
                                      />
                                      {question.followUpRequired && (
                                        <IconButton key={`iconBtn_${qi}`}
                                          title={question.showReason ? 'Collapse' : 'Expand'}
                                          onClick={() => {
                                            setSubQuestionResponse(prev => {
                                              const updated = [...prev];
                                              let qIndex = updated.findIndex(up => up.displayOrder === question.displayOrder)
                                              if (qIndex > -1) {
                                                updated[qIndex].showReason = !question.showReason;
                                              }
                                              return updated
                                            });
                                          }}
                                          size="medium">
                                          {question.showReason ? (
                                            <KeyboardDoubleArrowUpOutlinedIcon />
                                          ) : (
                                            <KeyboardDoubleArrowDownOutlinedIcon />
                                          )}
                                        </IconButton>
                                      )}
                                    </Box>
                                  </TableCell>
                                  {gridColums.map(
                                    (obj: NameProps, i: number) => (
                                      <TableCell
                                        sx={adminStyles.tableGridQuestionResponseCell}
                                        key={`trGridCol_0_${i}`}
                                      >
                                        <Radio
                                          key={`trGridCol_radio_${i}`}
                                          value={obj.id}
                                          onChange={() => {
                                            handleGridRadioChange(Number(obj.id), question.displayOrder);
                                          }}
                                          checked={(question.responseOptionId === obj.id)}
                                        />
                                      </TableCell>
                                    ),
                                  )}
                                </TableRow>
                                {question.followUpRequired && question.showReason &&
                                  (<TableRow key={`trGridQuReason_${qi}`}>
                                    <TableCell
                                      sx={adminStyles.tableGridQuestionReasonCell}
                                      key={`thGridQuReason_${qi}`}
                                      colSpan={gridColums.length + 1}
                                    >
                                      <>
                                        <Grid item xs={12} mt={1}>
                                          <Box
                                            sx={adminStyles.tableGridQuestionHeaderCellP}
                                            dangerouslySetInnerHTML={{
                                              __html: sanitizeReplaceWhiteSpace(
                                                questionDetail.additionalQuestionDetailList[qi].followUpQuestionTextHTML || "",
                                              ),
                                            }}
                                          />
                                        </Grid>
                                        <Grid item xs={12} mt={1}>
                                          <FormControl fullWidth>
                                            <TextField
                                              className="input-font-family"
                                              aria-label="comments textarea"
                                              multiline
                                              fullWidth
                                              placeholder="Please type here..."
                                              rows={4}
                                              value={question.responseText}
                                              onChange={e => {
                                                setSubQuestionResponse(prev => {
                                                  const updated = [...prev];
                                                  let qIndex = updated.findIndex(up => up.displayOrder === question.displayOrder)
                                                  if (qIndex > -1) {
                                                    updated[qIndex].responseText = e.target.value;
                                                  }
                                                  return updated
                                                });
                                              }}
                                              InputLabelProps={{ shrink: true }}
                                              inputProps={{
                                                id: "followupQuestion",
                                                maxLength: CPSConstants.MAX_LENGTH_TWO_THOUSAND,
                                              }}
                                            />
                                          </FormControl>
                                          <Grid
                                            container
                                            direction="row"
                                            justifyContent="flex-end"
                                            alignItems="flex-start">
                                            <Box>Character Count: {question.responseText.length}/{CPSConstants.MAX_LENGTH_TWO_THOUSAND}</Box>
                                          </Grid>
                                        </Grid>
                                      </>
                                    </TableCell>
                                  </TableRow>
                                  )}
                              </>
                            );
                          },
                        )}
                      </TableBody>
                    </Table>
                  </Grid>
                )}
                {questionDetail.questionType === SurveyQuestionTypes.Consent
                  && (
                    <Grid item xs={12} mt={1}>
                      <FormControl>
                        <RadioGroup
                          aria-labelledby="report-radio-buttons-group-label"
                          name={`${questionDetail.questionId}`}
                          value={selectedRadio ?? ''}
                          onChange={handleRadioChange}
                        >
                          {questionControlData?.consentOptions &&
                            questionControlData.consentOptions
                              .filter(item =>
                                questionDetail.optionIds &&
                                questionDetail.optionIds.split(',').some(x => Number(x) === item.id)
                              )
                              .map((item: NameProps, index: number) => (
                                <Box key={index} display="flex" alignItems="center" mb={1}>
                                  <Radio value={item.id} />
                                  <Typography variant="body1">{item.name}</Typography>
                                </Box>
                              ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  )}
                {questionDetail.questionType === SurveyQuestionTypes.Radio
                  && (
                    <Grid item xs={12} mt={1}>
                      <FormControl>
                        <RadioGroup
                          aria-labelledby="report-radio-buttons-group-label"
                          name={`${questionDetail.questionId}`}
                          value={selectedRadio ?? ''}
                          onChange={handleRadioChange}
                        >
                          {questionControlData?.radioButtonOptions &&
                            questionControlData.radioButtonOptions
                              .filter(item =>
                                questionDetail.optionIds &&
                                questionDetail.optionIds.split(',').some(x => Number(x) === item.id)
                              )
                              .map((item: NameProps, index: number) => (
                                <Box key={index} display="flex" alignItems="center" mb={1}>
                                  <Radio value={item.id} />
                                  <Typography variant="body1">{item.name}</Typography>
                                </Box>
                              ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  )}
                {(questionDetail.questionType === SurveyQuestionTypes.Numeric || questionDetail.questionType === SurveyQuestionTypes.RatingScale) && (
                  <Grid item xs={12} mt={1}>
                    {ratingScaleLevels.length > 0 && (
                      <Box sx={questionDetail.questionType === SurveyQuestionTypes.Numeric ? adminStyles.numericScaleBox : adminStyles.ratingScaleBox}>
                        <Slider
                          sx={ratingSacleValue == 0 ? adminStyles.ratingScaleNoValue : adminStyles.ratingScale}
                          marks={ratingScaleLevels}
                          getAriaValueText={valuetext}
                          min={questionDetail.questionType === SurveyQuestionTypes.Numeric ? (ratingScaleLevels?.[0].value ?? 1) : 1}
                          max={questionDetail.questionType === SurveyQuestionTypes.Numeric ? (ratingScaleLevels?.[(ratingScaleLevels.length - 1)].value ?? 1) : ratingScaleLevels.length}
                          step={1}
                          onChange={handleRatingScaleChange}
                          onChangeCommitted={handleRatingScaleChange}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(val) => { return (ratingScaleLevels.find(l => l.value == val)?.label ?? val) }}
                          value={ratingSacleValue}
                        />
                      </Box>
                    )}
                  </Grid>
                )}
                {questionDetail.questionType === SurveyQuestionTypes.Text && (
                  <Grid item xs={12} mt={1}>
                    <FormControl fullWidth>
                      <TextField
                        className="input-font-family"
                        aria-label="comments textarea"
                        multiline
                        fullWidth
                        rows={4}
                        placeholder="Please type here..."
                        value={textChar}
                        onChange={e => {
                          questionDetail.responseText = e.target.value;
                          setTextChar(e.target.value);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          maxLength: CPSConstants.MAX_LENGTH_TWO_THOUSAND,
                        }}
                      />
                    </FormControl>
                    <Grid
                      container
                      direction="row"
                      justifyContent="flex-end"
                      alignItems="flex-start">
                      <Box>Character Count: {textChar.length}/{CPSConstants.MAX_LENGTH_TWO_THOUSAND}</Box>
                    </Grid>
                  </Grid>
                )}
                {(questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ ||
                  questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ) && (
                    <Grid item xs={12}>
                      <Grid item xs={12} mt={1}>
                        <Box sx={{ ...adminStyles.mcqContainer, width: "100%" }}>
                          {chunkedOptions.map((column, colIndex) => (
                            <Box key={colIndex} sx={{ ...adminStyles.mcqColumn, ...(chunkedOptions.length > 1 ? { flex: 1 } : {}) }}>
                              {column.map((opt) => {
                                const isSelected = selectedMCQValues.includes(opt.fieldId);

                                return (
                                  <Box
                                    key={opt.fieldId}
                                    sx={{
                                      ...adminStyles.mcqOption,
                                      ...(chunkedOptions.length > 1 ? { minWidth: 0, width: "100%" } : {}),
                                      ...(isSelected ? adminStyles.mcqOptionSelected : {}),
                                    }}
                                  >
                                    {questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ ? (
                             <Radio
  checked={selectedMCQValues.includes(opt.fieldId)}
  onChange={() => {
    const newIds = [opt.fieldId];

    setSelectedMCQValues(newIds);
    questionDetail.responseOptionId = opt.fieldId;
    questionDetail.responseText = opt.fieldLabel;

    if (canAutoSaveMcq(newIds)) {
      setShowSubmitButton(false);
      submitMcqWithIds(newIds);
    } else {
      setShowSubmitButton(true);
      setSubmitButtonText();
    }
  }}
/>

                                    ) : (<Checkbox
                                      checked={selectedMCQValues.includes(opt.fieldId)}
                                      onChange={() => {

                                        const updatedIds: number[] = selectedMCQValues.includes(opt.fieldId)
                                          ? selectedMCQValues.filter((id: number) => id !== opt.fieldId)
                                          : [...selectedMCQValues, opt.fieldId];


                                        setSelectedMCQValues(updatedIds);

                                        const selectedLabels =
                                          (mcqOptions as mcqOption[])
                                            .filter((o: mcqOption) => updatedIds.includes(o.fieldId))
                                            .map((o: mcqOption) => o.fieldLabel);

                                        questionDetail.mcqResponseOptionsId = updatedIds.join(",");
                                        questionDetail.responseText = selectedLabels.join(",");
                                      }}
                                    />
                                    )}

                                    <Typography sx={adminStyles.mcqLabel}>
                                      {opt.fieldLabel}
                                    </Typography>

                                    {opt.displayToolTip && (
                                      <LightTooltip
                                        title={
                                          <Box
                                            dangerouslySetInnerHTML={{
                                              __html: DOMPurify.sanitize(opt.toolTipText || "")
                                            }}
                                          />
                                        }
                                        placement="right"
                                      >
                                        <InfoOutlinedIcon sx={adminStyles.InfoIconStyle} />
                                      </LightTooltip>
                                    )}

                                  </Box>
                                );
                              })}
                            </Box>
                          ))}

                        </Box>
                      </Grid>
                      {mcqOptions.some(
                        opt =>
                          opt.fieldLabel?.toLowerCase() === CPSConstants.OTHER &&
                          selectedMCQValues.includes(opt.fieldId)
                      ) && (
                                            <Grid item xs={12} mt={1}>

                          <Grid item xs={12} mt={2}>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              placeholder="Please type here..."

                              value={otherText}
                              onChange={e => {
                                setOtherText(e.target.value);
                                questionDetail.otherOptionMCQText = e.target.value;
                              }}
                            />
                          </Grid>
                               <Grid
                        container
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="flex-start">
                        <Box>Character Count: {otherText.length}/{CPSConstants.MAX_LENGTH_HUNDRED}</Box>
                      </Grid>
                      </Grid>
                        )}
                    </Grid>
                    
                  )}

                {showFollowUpQuestion && questionDetail.questionType !== SurveyQuestionTypes.Grid && questionDetail.questionType !== 'SurveyDetails' && (
                  <>
                    <Grid item xs={12} mt={1}>
                      <Box
                        sx={adminStyles.tableGridQuestionHeaderCellP}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeReplaceWhiteSpace(
                            questionDetail.followUpQuestionTextHTML || "",
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                      <FormControl fullWidth>
                        <TextField
                          className="input-font-family"
                          aria-label="comments textarea"
                          multiline
                          fullWidth
                          placeholder="Please type here..."
                          rows={4}
                          value={followupChar}
                          onChange={e => {
                            questionDetail.responseText = e.target.value;
                            setFollowupChar(e.target.value);
                          }}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            id: "followupQuestion",
                            maxLength: CPSConstants.MAX_LENGTH_TWO_THOUSAND,
                          }}
                        />
                      </FormControl>
                      <Grid
                        container
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="flex-start">
                        <Box>Character Count: {followupChar.length}/{CPSConstants.MAX_LENGTH_TWO_THOUSAND}</Box>
                      </Grid>
                    </Grid>
                  </>
                )}
                {showMultiFollowup && ((questionDetail.questionType == SurveyQuestionTypes.MultiSelectMCQ || questionDetail.questionType == SurveyQuestionTypes.SingleSelectMCQ)) && (
                  <>
                  
                    <Grid item xs={12} mt={1}>
                      <Box
                        sx={adminStyles.tableGridQuestionHeaderCellP}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeReplaceWhiteSpace(
                            questionDetail.followUpQuestionTextHTML || "",
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} mt={1}>
                      <FormControl fullWidth>
                        <TextField
                          multiline
                          rows={4}
                          placeholder="Please type here..."

                          value={followUpText}
                          onChange={e => {

                            setFollowUpText(e.target.value);
                            questionDetail.followUpResponseText = e.target.value;


                          }}
                        />
                      </FormControl>
                      <Grid
                        container
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="flex-start">
                        <Box>Character Count: {followUpText.length}/{CPSConstants.MAX_LENGTH_TWO_THOUSAND}</Box>
                      </Grid>
                    </Grid>
                  </>
                )}
              </>)}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 0 }}>
          <Grid container justifyContent="space-between" alignItems="center" pt={2} pb={3} pl={3.4} pr={(questionDetail.questionType === SurveyQuestionTypes.SingleSelectMCQ || questionDetail.questionType === SurveyQuestionTypes.MultiSelectMCQ || questionDetail.questionType === SurveyQuestionTypes.Grid) ? 4.3 : 3.2}>
            <Grid item>
              <Grid container spacing={2}>
                {showPreviousButton && (
                  <Grid item>
                    <FormButton
                      onClick={previousSubmit}
                      variant="contained"
                      size="small"
                      outlet="Previous"
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            {showSubmitButton && (
              <>
                <Grid item > 
                  <FormButton
                    onClick={responseSubmit}
                    variant="contained"
                    size="small"
                    outlet={submitBtnText ? submitBtnText : "Submit"}
                  />
                </Grid>
              </>
            )}
          </Grid>

        </DialogActions>
      </Dialog>
    </>
  );
};

export default SurveyQuestionDialog;
